import React, { FC } from "react";
import styled from "@emotion/styled";
import { AnimatePresence, motion } from "motion/react";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { SessionArtifactNoteRenderer } from "../session-artifact-note-renderer";
import { useMDSSession } from "../../../context";
import { MDSSessionArtifact } from "@/mds/components/sessions/context/mds-session-context.types";
import {
  ArtifactV1SessionPartKind,
  NoteCreatedV1ArtifactPayload,
  NoteUpdatedV1ArtifactPayload,
} from "@/mds/components/sessions/session.types";
import { DateTime } from "luxon";

interface Props {
  isVisible: boolean;
}

export const SessionArtifactViewer: FC<Props> = ({ isVisible }) => {
  const { state, actions } = useMDSSession();

  return (
    <AnimatePresence>
      {isVisible && state.activeArtifact && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.08 }}
        >
          <Container>
            <Header>
              <CloseButton onClick={actions.closeArtifact}>
                <MdsIcon kind={MdsIconKind.Exit} />
              </CloseButton>
            </Header>
            <Content>
              <ArtifactRenderer artifact={state.activeArtifact} />
            </Content>
          </Container>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

const ArtifactRenderer = ({ artifact }: { artifact: MDSSessionArtifact }) => {
  switch (artifact.kind) {
    case ArtifactV1SessionPartKind.NoteCreatedV1: {
      const payload = artifact.payload as NoteCreatedV1ArtifactPayload;

      return (
        <SessionArtifactNoteRenderer
          data={{
            noteInfo: {
              noteId: payload.note_id,
              createdAt: DateTime.fromISO(payload.note_created_at),
              updatedAt: DateTime.fromISO(payload.note_updated_at),
              encodedContent: payload.encoded_content,
              primaryLabel: payload.primary_label,
              secondaryLabel: payload.secondary_label,
              markdownContent: payload.markdown_content,
            },
          }}
        />
      );
    }
    case ArtifactV1SessionPartKind.NoteUpdatedV1: {
      const payload = artifact.payload as NoteUpdatedV1ArtifactPayload;

      return (
        <SessionArtifactNoteRenderer
          data={{
            noteInfo: {
              noteId: payload.note_id,
              createdAt: DateTime.fromISO(payload.note_created_at),
              updatedAt: DateTime.fromISO(payload.note_updated_at),
              encodedContent: payload.encoded_content,
              primaryLabel: payload.primary_label,
              secondaryLabel: payload.secondary_label,
              markdownContent: payload.markdown_content,
            },
            previousNoteInfo: {
              noteId: payload.note_id,
              createdAt: DateTime.fromISO(payload.note_created_at),
              updatedAt: DateTime.fromISO(payload.note_updated_at),
              encodedContent: payload.prev_encoded_content,
              primaryLabel: payload.prev_primary_label,
              secondaryLabel: payload.prev_secondary_label,
              markdownContent: payload.prev_markdown_content,
            },
            showDifferences: true,
          }}
        />
      );
    }
  }
};

const Overlay = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  z-index: 1000;
`;

const Container = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 20px 60px rgba(0, 0, 0, 0.15);
  width: 100%;
  height: calc(100% - 60px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey.x100};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.medium}px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey.x50};
  }

  &:active {
    background-color: ${({ theme }) => theme.colors.grey.x100};
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.grey.x400};
  }
`;

const Content = styled.div`
  flex: 1;
  overflow: auto;
`;
